import { useEffect, useState, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import usePermisos from '../../hooks/usePermisos';
import DataTable from '../../components/Panel/DataTable';
import BotonAccionIcono from '../../components/Panel/BotonAccionIcono';
import usePaginacionLocal from '../../hooks/usePaginacionLocal';
import useFiltroBusqueda from '../../hooks/useFiltroBusqueda';
import PanelShell from './PanelShell';
import '../../styles/Panel.css';

const ROL_VACIO = { rol_chNombre: '', rol_chDescripcion: '', permisos: [] };

/**
 * Grupo de permisos de un módulo (HU-012, inspirado en el panel de roles de
 * `REFERENCIAS/FRONTEND/src/views/RolesView.jsx`) — chevron para expandir/
 * contraer, contador "X / Y" seleccionados, y un toggle "Todo" que marca o
 * desmarca el grupo entero de una vez sin tener que tocar cada checkbox.
 */
const GrupoPermisosRol = ({ modulo, permisos, seleccionados, onCambiar, soloLectura, abierto, onToggleAbierto }) => {
  const marcados = permisos.filter((p) => seleccionados.includes(p.per_inId));
  const todoMarcado = marcados.length === permisos.length;

  const marcarTodo = (marcar) => {
    const otros = seleccionados.filter((id) => !permisos.some((p) => p.per_inId === id));
    onCambiar(marcar ? [...otros, ...permisos.map((p) => p.per_inId)] : otros);
  };

  return (
    <div className="panel-rol-permisos-grupo">
      <div className="panel-rol-permisos-grupo-cabecera" onClick={onToggleAbierto}>
        <i className={`fas fa-chevron-right panel-rol-permisos-chevron ${abierto ? 'panel-rol-permisos-chevron-abierto' : ''}`}></i>
        <span className="panel-rol-permisos-grupo-titulo">{modulo}</span>
        <span className="panel-rol-permisos-grupo-conteo">{marcados.length} / {permisos.length}</span>
        {!soloLectura && (
          <label className="panel-toggle" onClick={(e) => e.stopPropagation()}>
            <input type="checkbox" checked={todoMarcado} onChange={(e) => marcarTodo(e.target.checked)} />
            <span className="panel-toggle-riel"><span className="panel-toggle-bola"></span></span>
            <span className="panel-toggle-etiqueta">Todo</span>
          </label>
        )}
      </div>
      {abierto && (
        <div className="panel-rol-permisos-grid">
          {permisos.map((p) => (
            <label key={p.per_inId} className="panel-rol-permiso-item">
              <input
                type="checkbox"
                checked={seleccionados.includes(p.per_inId)}
                disabled={soloLectura}
                onChange={() => onCambiar(
                  seleccionados.includes(p.per_inId)
                    ? seleccionados.filter((id) => id !== p.per_inId)
                    : [...seleccionados, p.per_inId]
                )}
              />
              <span>{p.per_chDescripcion}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Gestión de roles y permisos (HU-009-A Fase 3; panel lateral en HU-012) —
 * pantalla separada de Usuarios y Configuración. "Crear rol"/"Editar" abren
 * el mismo panel a la derecha de la tabla (antes eran dos modales
 * distintos: uno para nombre/descripción y otro aparte para permisos) —
 * nombre, descripción y permisos se guardan en un solo paso. Los roles de
 * sistema (`rol_boEsSistema`) no se pueden renombrar ni eliminar, pero sí
 * se les puede editar la descripción y reasignar sus permisos. Exclusiva de
 * personal administrativo (gateada por `RutaProtegidaPanel`, no acá).
 */
const GestionRoles = () => {
  const { loading, error, listarPermisos, listarRoles, crearRol, actualizarRol, eliminarRol } = usePermisos();

  const [roles, setRoles] = useState([]);
  const { busqueda, setBusqueda, itemsFiltrados } = useFiltroBusqueda(
    roles,
    (r) => `${r.rol_chNombre} ${r.rol_chDescripcion}`
  );
  const { datosPagina, paginacion } = usePaginacionLocal(itemsFiltrados);
  const [catalogoPermisos, setCatalogoPermisos] = useState([]);
  const [panel, setPanel] = useState(null); // null | 'crear' | rol completo
  const [form, setForm] = useState(ROL_VACIO);
  const [gruposAbiertos, setGruposAbiertos] = useState(() => new Set());
  const [mensaje, setMensaje] = useState('');

  const cargar = useCallback(async () => {
    const [resRoles, resPermisos] = await Promise.all([listarRoles(), listarPermisos()]);
    if (resRoles.success) setRoles(resRoles.roles);
    if (resPermisos.success) setCatalogoPermisos(resPermisos.permisos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Catálogo agrupado por módulo, para pintar el panel en secciones en vez
  // de una lista plana de 17+ checkboxes sin ningún orden.
  const permisosPorModulo = useMemo(() => {
    const grupos = {};
    catalogoPermisos.forEach((p) => {
      if (!grupos[p.per_chModulo]) grupos[p.per_chModulo] = [];
      grupos[p.per_chModulo].push(p);
    });
    return grupos;
  }, [catalogoPermisos]);

  // A diferencia del proyecto de referencia (donde un rol de sistema es
  // 100% de solo lectura), acá los 5 roles de sistema SÍ pueden reasignar
  // sus permisos (decisión de HU-009-A) — solo el nombre queda protegido.
  const soloLecturaPermisos = false;
  const esEdicion = panel && panel !== 'crear';
  const esSistema = esEdicion && panel.rol_boEsSistema;

  const abrir = (rol) => {
    setMensaje('');
    setPanel(rol || 'crear');
    const permisosIniciales = rol ? rol.permisos.map((p) => p.per_inId) : [];
    setForm({
      rol_chNombre: rol?.rol_chNombre || '',
      rol_chDescripcion: rol?.rol_chDescripcion || '',
      permisos: permisosIniciales,
    });
    // Al abrir, expande solo los módulos que ya tienen algún permiso
    // marcado — evita mostrar 5+ acordeones abiertos de entrada.
    const modulosConSeleccion = Object.entries(permisosPorModulo)
      .filter(([, permisos]) => permisos.some((p) => permisosIniciales.includes(p.per_inId)))
      .map(([modulo]) => modulo);
    setGruposAbiertos(new Set(modulosConSeleccion));
  };

  const cerrar = () => setPanel(null);

  const toggleGrupo = (modulo) => setGruposAbiertos((actual) => {
    const nuevo = new Set(actual);
    if (nuevo.has(modulo)) nuevo.delete(modulo); else nuevo.add(modulo);
    return nuevo;
  });

  const guardar = async (evento) => {
    evento.preventDefault();
    setMensaje('');
    const datos = {
      rol_chDescripcion: form.rol_chDescripcion,
      permisos_ids: form.permisos,
    };
    if (!esEdicion) datos.rol_chNombre = form.rol_chNombre;

    const resultado = esEdicion
      ? await actualizarRol(panel.rol_inId, datos)
      : await crearRol(datos);

    if (resultado.success) {
      cerrar();
      cargar();
    } else {
      setMensaje(resultado.error);
    }
  };

  const handleEliminar = async (rol) => {
    const confirmacion = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar rol?',
      text: `"${rol.rol_chNombre}" — esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#e63946',
    });
    if (!confirmacion.isConfirmed) return;
    const resultado = await eliminarRol(rol.rol_inId);
    if (resultado.success) {
      if (esEdicion && panel.rol_inId === rol.rol_inId) cerrar();
      cargar();
    } else {
      setMensaje(resultado.error);
    }
  };

  const columnas = useMemo(() => [
    {
      Header: 'Rol',
      accessor: 'rol_chNombre',
      Cell: ({ row, value }) => (
        <span>
          {value}{' '}
          {row.original.rol_boEsSistema && <span className="panel-rol-badge-sistema">Sistema</span>}
        </span>
      ),
    },
    { Header: 'Descripción', accessor: 'rol_chDescripcion' },
    {
      Header: 'Permisos',
      id: 'permisos',
      accessor: (r) => r.permisos.length,
      Cell: ({ value }) => `${value} / ${catalogoPermisos.length || 0}`,
    },
    {
      Header: 'Acciones',
      id: 'acciones',
      Cell: ({ row }) => (
        <div className="panel-gu-fila-acciones">
          <BotonAccionIcono icono="fa-pen" variante="editar" titulo="Editar" onClick={() => abrir(row.original)} />
          {!row.original.rol_boEsSistema && (
            <BotonAccionIcono icono="fa-trash" variante="eliminar" titulo="Eliminar" onClick={() => handleEliminar(row.original)} />
          )}
        </div>
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [catalogoPermisos, permisosPorModulo]);

  return (
    <PanelShell>
      <div className="panel-gu-cabecera">
        <h2>Roles y permisos</h2>
        <div className="panel-gu-acciones">
          <button type="button" className="panel-boton-header-accion" onClick={() => abrir(null)}>
            <i className="fas fa-plus"></i> Nuevo rol
          </button>
        </div>
      </div>

      <div className="panel-gu-filtros">
        <input
          className="panel-input"
          placeholder="Buscar por nombre o descripción…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
      </div>

      {mensaje && <p className="panel-gu-mensaje">{mensaje}</p>}
      {error && <p className="panel-gu-mensaje">{error}</p>}

      <div className="panel-roles-master-detail">
        <div className="panel-roles-master">
          <DataTable
            columns={columnas}
            data={datosPagina}
            filaVacia={loading ? 'Cargando…' : 'No hay roles.'}
            paginacion={paginacion}
          />
        </div>

        {panel && (
          <div className="panel-roles-detail">
            <form onSubmit={guardar}>
              <div className="panel-roles-detail-cabecera">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label className="panel-label">Nombre del rol</label>
                  <input
                    className="panel-input"
                    required
                    disabled={esSistema}
                    value={form.rol_chNombre}
                    onChange={(e) => setForm({ ...form, rol_chNombre: e.target.value.toUpperCase() })}
                  />
                </div>
                <button type="button" className="panel-icono-boton" onClick={cerrar} aria-label="Cerrar">
                  <i className="fas fa-xmark"></i>
                </button>
              </div>
              {esSistema && <p className="panel-texto-ayuda">Es un rol de sistema — el nombre no se puede cambiar.</p>}

              <label className="panel-label">Descripción</label>
              <input
                className="panel-input"
                value={form.rol_chDescripcion}
                onChange={(e) => setForm({ ...form, rol_chDescripcion: e.target.value })}
              />

              <div className="panel-rol-permisos-separador">
                <span>PERMISOS</span>
                <div></div>
                <button type="button" className="panel-rol-limpiar-todo" onClick={() => setForm({ ...form, permisos: [] })}>
                  Limpiar todo
                </button>
              </div>

              <div className="panel-rol-permisos-lista">
                {Object.entries(permisosPorModulo).map(([modulo, permisos]) => (
                  <GrupoPermisosRol
                    key={modulo}
                    modulo={modulo}
                    permisos={permisos}
                    seleccionados={form.permisos}
                    onCambiar={(nuevos) => setForm({ ...form, permisos: nuevos })}
                    soloLectura={soloLecturaPermisos}
                    abierto={gruposAbiertos.has(modulo)}
                    onToggleAbierto={() => toggleGrupo(modulo)}
                  />
                ))}
              </div>

              <div className="panel-gu-modal-botones">
                <button type="button" className="panel-boton-secundario" onClick={cerrar}>Cancelar</button>
                <button type="submit" className="panel-boton-primario" disabled={loading}>
                  {loading ? 'Guardando…' : 'Guardar rol'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </PanelShell>
  );
};

export default GestionRoles;
